import { InfoOutlined } from '@mui/icons-material';
import {
  Box,
  IconButton,
  Popover,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMemo, useState } from 'react';

const sumField = (rows, field) =>
  rows.reduce((acc, r) => acc + (r[field] ?? 0), 0);

/**
 * Reusable popover that displays per-country stock breakdown with a total row.
 *
 * Two trigger modes:
 * - Default (no children): renders an info-icon button.
 * - With children: wraps children in a clickable box (used by TableWidget
 *   so the cell value itself is the click target, no extra icon).
 */
const StockBreakdownPopover = ({ stockBreakdown, children }) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const filteredBreakdown = useMemo(() => {
    if (!stockBreakdown) return [];
    return stockBreakdown.filter(
      (r) =>
        (r.readyForShipment ?? 0) !== 0 ||
        (r.totalBlockedCars ?? 0) !== 0 ||
        (r.currentStock ?? 0) !== 0 ||
        (r.forecastInflow ?? 0) !== 0 ||
        (r.actualInflow ?? 0) !== 0
    );
  }, [stockBreakdown]);

  const totals = useMemo(() => {
    if (filteredBreakdown.length === 0) return null;
    return {
      readyForShipment: sumField(filteredBreakdown, 'readyForShipment'),
      totalBlockedCars: sumField(filteredBreakdown, 'totalBlockedCars'),
      currentStock: sumField(filteredBreakdown, 'currentStock'),
      forecastInflow: sumField(filteredBreakdown, 'forecastInflow'),
      actualInflow: sumField(filteredBreakdown, 'actualInflow'),
    };
  }, [filteredBreakdown]);

  if (filteredBreakdown.length === 0) {
    return children ?? null;
  }

  const open = Boolean(anchorEl);

  const hasCustomTrigger = children !== undefined && children !== null;

  const trigger = hasCustomTrigger ? (
    <Box
      onClick={(e) => setAnchorEl(e.currentTarget)}
      sx={{
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        width: '100%',
      }}
    >
      {children}
    </Box>
  ) : (
    <Tooltip title='View per-country stock breakdown'>
      <IconButton
        size='small'
        color='info'
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{ ml: 0.5, p: 0.25 }}
      >
        <InfoOutlined fontSize='small' />
      </IconButton>
    </Tooltip>
  );

  return (
    <>
      {trigger}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <TableContainer sx={{ maxHeight: 400, p: 1 }}>
          <Typography variant='subtitle2' sx={{ px: 1, pt: 0.5, pb: 1 }}>
            Stock Breakdown by Country
          </Typography>
          <Table size='small' stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Country</TableCell>
                <TableCell align='right'>Ready for Shipment</TableCell>
                <TableCell align='right'>Blocked</TableCell>
                <TableCell align='right'>Current Stock</TableCell>
                <TableCell align='right'>Forecast Inflow</TableCell>
                <TableCell align='right'>Actual Inflow</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredBreakdown.map((row) => (
                <TableRow key={row.countryCode}>
                  <TableCell>{row.countryName ?? row.countryCode}</TableCell>
                  <TableCell align='right'>
                    {row.readyForShipment ?? '-'}
                  </TableCell>
                  <TableCell align='right'>
                    {row.totalBlockedCars ?? '-'}
                  </TableCell>
                  <TableCell align='right'>{row.currentStock ?? '-'}</TableCell>
                  <TableCell align='right'>
                    {row.forecastInflow ?? '-'}
                  </TableCell>
                  <TableCell align='right'>{row.actualInflow ?? '-'}</TableCell>
                </TableRow>
              ))}
              {/* Total row */}
              <TableRow
                sx={{
                  '& td': {
                    fontWeight: 700,
                    borderTop: 2,
                    borderColor: 'divider',
                  },
                }}
              >
                <TableCell>Total</TableCell>
                <TableCell align='right'>{totals.readyForShipment}</TableCell>
                <TableCell align='right'>{totals.totalBlockedCars}</TableCell>
                <TableCell align='right'>{totals.currentStock}</TableCell>
                <TableCell align='right'>{totals.forecastInflow}</TableCell>
                <TableCell align='right'>{totals.actualInflow}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Popover>
    </>
  );
};

export default StockBreakdownPopover;
